import { Deployment } from "@momo/types";
import { type FirebaseApp, getApps, initializeApp } from "firebase/app";
import {
	type AppCheck,
	initializeAppCheck,
	ReCaptchaEnterpriseProvider,
} from "firebase/app-check";
import { type Auth, connectAuthEmulator, getAuth } from "firebase/auth";
import {
	connectFirestoreEmulator,
	initializeFirestore,
} from "firebase/firestore";
import { connectFunctionsEmulator, getFunctions } from "firebase/functions";
import { connectStorageEmulator, getStorage } from "firebase/storage";
import { EmulatorEndpoints } from "./emulator-endpoints.ts";

declare global {
	var __BROWSER_BOOTSTRAP__: unknown;
	var __FIREBASE_EMULATOR_ENDPOINTS__: unknown;
}

// Assigned by the blocking classic <script src="/bootstrap.js"> each page loads
// before its modules; the build itself carries no project settings. Without
// them the page shows one plain notice in place of the splash and the throw
// stops module evaluation, so nothing boots half-configured.
export function browserBootstrapFromGlobal() {
	const parsed = Deployment.BrowserBootstrapSchema.safeParse(
		globalThis.__BROWSER_BOOTSTRAP__,
	);
	if (parsed.success) return parsed.data;
	const notice =
		globalThis.__BROWSER_BOOTSTRAP__ === undefined
			? "This site could not load its settings (/bootstrap.js). Redeploy the site; locally, run pnpm local:prepare."
			: "This site's settings (/bootstrap.js) are invalid. Redeploy the site; locally, run pnpm local:prepare.";
	if (typeof document !== "undefined") {
		const element = document.createElement("p");
		element.textContent = notice;
		// No theme exists before boot, so the notice pins its own legible palette.
		element.style.cssText =
			"margin:0;padding:24px;min-height:100dvh;box-sizing:border-box;background:#fff;color:#000;font:16px/1.5 system-ui,sans-serif";
		document.body.replaceChildren(element);
	}
	throw new Error(
		globalThis.__BROWSER_BOOTSTRAP__ === undefined
			? notice
			: `${notice} ${parsed.error.issues
					.map(
						(issue) =>
							`${issue.path.join(".") || "settings"}: ${issue.message}`,
					)
					.join("; ")}`,
	);
}

const appChecks = new WeakMap<FirebaseApp, AppCheck>();

function readEmulatorEndpoints() {
	return EmulatorEndpoints.parse(
		globalThis.__FIREBASE_EMULATOR_ENDPOINTS__ === undefined
			? {
					host: "127.0.0.1",
					auth: 9119,
					firestore: 8102,
					storage: 9219,
					functions: 5021,
				}
			: globalThis.__FIREBASE_EMULATOR_ENDPOINTS__,
	);
}

function connectAuth(
	auth: Auth,
	endpoints: ReturnType<typeof readEmulatorEndpoints>,
) {
	const host = endpoints.host === "::1" ? "[::1]" : endpoints.host;
	if (auth.emulatorConfig) {
		if (
			auth.emulatorConfig.host !== host ||
			auth.emulatorConfig.protocol !== "http" ||
			auth.emulatorConfig.port !== endpoints.auth
		)
			throw new Error(
				"Existing Auth emulator does not match the configured endpoints",
			);
		return;
	}
	connectAuthEmulator(auth, `http://${host}:${endpoints.auth}`, {
		disableWarnings: true,
	});
}

/** Restores identity without starting attestation or the data/callable clients. */
export function initFirebaseAuth(
	bootstrap: Deployment.BrowserBootstrap,
	{ emulator = false }: { emulator?: boolean } = {},
) {
	const parsed = Deployment.BrowserBootstrapSchema.parse(bootstrap);
	const endpoints = emulator ? readEmulatorEndpoints() : null;
	// Named apps can belong to a harness mounted for another project.
	const existing = getApps().find((app) => app.name === "[DEFAULT]");
	if (existing && existing.options.projectId !== parsed.firebase.projectId)
		throw new Error(
			`Existing Firebase app project ${existing.options.projectId ?? "unknown"} does not match bootstrap project ${parsed.firebase.projectId}`,
		);
	const app = existing ?? initializeApp(parsed.firebase);
	const auth = getAuth(app);
	if (endpoints) connectAuth(auth, endpoints);
	return { app, auth, isNew: existing === undefined };
}

// A function, not a module side-effect: the caller decides when the app is
// created and whether it talks to an emulator. A side-effect init runs at import
// time, which is before a test can point it anywhere.
//
// Functions is created here, alongside the app: the component registers on the
// @firebase/app copy that owns the app, and a later getProvider('functions')
// resolves only on that same container. The caller supplies the callable region.
export function initFirebaseClient(
	bootstrap: Deployment.BrowserBootstrap,
	{
		emulator = false,
	}: {
		emulator?: boolean;
	} = {},
) {
	const parsed = Deployment.BrowserBootstrapSchema.parse(bootstrap);
	const { app, auth, isNew } = initFirebaseAuth(parsed);

	// App Check is enforced on Firestore and Storage. The token is fetched eagerly
	// at page load rather than lazily at the first write, so the Pay gesture never
	// waits on an attestation round-trip. Skipped against the emulator, which does
	// not verify tokens.
	if (!appChecks.has(app) && !emulator) {
		appChecks.set(
			app,
			initializeAppCheck(app, {
				provider: new ReCaptchaEnterpriseProvider(
					parsed.appCheck.recaptchaSiteKey,
				),
				isTokenAutoRefreshEnabled: true,
			}),
		);
	}

	// Against the Firestore emulator the default WebChannel transport stalls in an
	// automated Chromium context — the streaming handshake never completes, so the
	// default long-polling auto-detect never sees the response that would trigger
	// its upgrade, and the SDK goes offline after 10s. Force long-polling on the
	// emulator path only; production keeps WebChannel.
	const db = initializeFirestore(app, {
		ignoreUndefinedProperties: true,
		...(emulator ? { experimentalForceLongPolling: true } : {}),
	});

	return {
		app,
		appCheck: appChecks.get(app),
		defaultHostingOrigin: parsed.defaultHostingOrigin,
		auth,
		db,
		storage: getStorage(app),
		functions: getFunctions(app, parsed.functionsRegion),
		isNew,
	};
}

// Ports match firebase/firebase.json. Every emulator the suite runs is
// wired: anything left out silently reaches production from a dev machine.
export function connectEmulators({
	auth,
	db,
	storage,
	functions,
}: ReturnType<typeof initFirebaseClient>) {
	const emulatorEndpoints = readEmulatorEndpoints();
	connectAuth(auth, emulatorEndpoints);
	connectFirestoreEmulator(
		db,
		emulatorEndpoints.host,
		emulatorEndpoints.firestore,
	);
	connectStorageEmulator(
		storage,
		emulatorEndpoints.host,
		emulatorEndpoints.storage,
	);
	connectFunctionsEmulator(
		functions,
		emulatorEndpoints.host,
		emulatorEndpoints.functions,
	);
	return emulatorEndpoints;
}
